import { useCallback, useEffect, useRef, useState } from 'react';
import type { MapObject, PersistedStroke, Stroke } from '@etown/shared';
import { Modal } from '../../components/Modal';
import { BOARD_HEIGHT, BOARD_WIDTH, clearBoard as paintBlank, drawStroke as paintStroke } from '../../lib/whiteboardCanvas';
import { useOffice } from '../office/OfficeContext';
import './objects.css';

const COLORS = ['#1e2430', '#ff6b5e', '#2ec4b6', '#9b8cff', '#f2b33d'];
const WIDTHS = [0.004, 0.008, 0.016];

/**
 * Gemeinsames Whiteboard: Striche werden lokal gezeichnet, beim Absetzen
 * an den Server geschickt (Persistenz + Broadcast) und von anderen live
 * empfangen. Koordinaten sind auf 0..1 normalisiert — aufloesungsunabhaengig.
 */
export function WhiteboardModal({ object, onClose }: { object: MapObject; onClose: () => void }) {
  const { socket } = useOffice();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef<number[] | null>(null);
  const [color, setColor] = useState(COLORS[0]!);
  const [width, setWidth] = useState(WIDTHS[1]!);

  const drawStroke = useCallback((stroke: Stroke): void => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) paintStroke(ctx, stroke);
  }, []);

  const clearCanvas = useCallback((): void => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) paintBlank(ctx);
  }, []);

  // Initialer Zustand + Live-Striche anderer
  useEffect(() => {
    clearCanvas();
    socket.emit('whiteboard:load', { objectId: object.id }, (res) => {
      if (res.ok) {
        for (const persisted of res.data.strokes) drawStroke(persisted.stroke);
      }
    });
    const onStroke = (payload: PersistedStroke): void => {
      if (payload.objectId === object.id) drawStroke(payload.stroke);
    };
    const onCleared = (payload: { objectId: string }): void => {
      if (payload.objectId === object.id) clearCanvas();
    };
    socket.on('whiteboard:stroke', onStroke);
    socket.on('whiteboard:cleared', onCleared);
    return () => {
      socket.off('whiteboard:stroke', onStroke);
      socket.off('whiteboard:cleared', onCleared);
    };
  }, [socket, object.id, drawStroke, clearCanvas]);

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    const y = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1);
    return [x, y];
  };

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = [...pointFromEvent(event)];
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const points = drawingRef.current;
    if (!points) return;
    const [x, y] = pointFromEvent(event);
    const prevX = points[points.length - 2]!;
    const prevY = points[points.length - 1]!;
    // Punkte ausduennen: erst ab minimaler Bewegung aufnehmen
    if (Math.hypot(x - prevX, y - prevY) < 0.004) return;
    points.push(x, y);
    drawStroke({ points: [prevX, prevY, x, y], color, width });
  };

  const onPointerUp = (): void => {
    const points = drawingRef.current;
    drawingRef.current = null;
    if (!points || points.length < 4) return;
    socket.emit('whiteboard:stroke', { objectId: object.id, stroke: { points, color, width } }, () => {});
  };

  const clearBoard = (): void => {
    socket.emit('whiteboard:clear', { objectId: object.id }, () => {});
    clearCanvas();
  };

  return (
    <Modal title="Whiteboard" onClose={onClose} wide>
      <div className="whiteboard__toolbar">
        <div className="whiteboard__colors" role="group" aria-label="Farbe">
          {COLORS.map((c) => (
            <button
              key={c}
              className={`whiteboard__color ${c === color ? 'whiteboard__color--active' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
              aria-label={`Farbe ${c}`}
            />
          ))}
        </div>
        <div className="whiteboard__widths" role="group" aria-label="Strichstärke">
          {WIDTHS.map((w) => (
            <button
              key={w}
              className={`whiteboard__width ${w === width ? 'whiteboard__width--active' : ''}`}
              onClick={() => setWidth(w)}
              aria-label={`Stärke ${w}`}
            >
              <span style={{ width: w * 800, height: w * 800 }} />
            </button>
          ))}
        </div>
        <button className="whiteboard__clear" onClick={clearBoard}>
          Alles löschen
        </button>
      </div>
      <canvas
        ref={canvasRef}
        className="whiteboard__canvas"
        width={BOARD_WIDTH}
        height={BOARD_HEIGHT}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      />
    </Modal>
  );
}
